import React from 'react'
import { ProductSectorMapping, ValidationIssue } from 'api'
import styled from '@emotion/styled'
import { Locale, Translation } from 'locale'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { useUpdateHandler } from 'view/hooks'
import { CountrySelectboxField } from 'view/shared/entity/CountrySelectbox'
import { AssetTypeSelectboxField } from 'view/shared/entity/AssetTypeSelectbox'
import { BooleanSelectboxField, CurrencySelectboxField, ProductSectorSelectboxField } from 'view/shared/entity'
import { createProductGetterSelector } from 'selectors/productSelectors'
import { useSelector } from 'react-redux'
import { RefinitivAssetTypeSelectboxField } from 'view/shared/entity/RefinitivAssetTypeSelectbox'
import { AssetTypeHelper } from 'model'

interface SectorDialogComponentProps {
  title: string
  productSectorMapping: ProductSectorMapping
  id: string
  validationIssues: ValidationIssue[]
  onSave: () => void
  onClose: () => void
  onUpdate: (partialProductSector: Partial<ProductSectorMapping>) => void
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  min-height: 250px;
  overflow-y: auto;
  width: 650px;
`

export const SectorMappingDialogComponent: React.FC<SectorDialogComponentProps> = ({
  title,
  productSectorMapping,
  validationIssues,
  id,
  onClose,
  onSave,
  onUpdate
}) => {
  const updateHandler = useUpdateHandler(onUpdate)

  const getProduct = useSelector(createProductGetterSelector)
  const product = getProduct(productSectorMapping.productSectorId || '')
  return (
    <ModalWindow title={title}>
      <Content>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-Product'}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_RULE_ID)}
              value={id}
              onUpdate={() => {}}
              validationIssues={validationIssues || undefined}
              fieldName="ruleId"
              readOnly={true}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-RuleName'}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_RULE_NAME)}
              value={productSectorMapping.ruleName}
              onUpdate={updateHandler('ruleName')}
              validationIssues={validationIssues || undefined}
              fieldName="ruleName"
              maxLength={250}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <ProductSectorSelectboxField
              dataTestId={'SectorMappingDialog-TextInputField-Product'}
              label={Locale.translate(Translation.TC_PRODUCT_SECTORS)}
              value={productSectorMapping.productSectorId}
              onUpdate={updateHandler('productSectorId')}
              fieldName="productSectorId"
              validationIssues={validationIssues || undefined}
              bothMarkets={true}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-Product'}
              label={Locale.translate(Translation.TC_PRODUCT)}
              value={product?.name}
              onUpdate={() => {}}
              fieldName="Product"
              readOnly={true}
            />
          </DialogField>
          <DialogField>
            <AssetTypeSelectboxField
              dataTestId={'SectorMappingDialog-AssetTypeSelectboxField-AssetType'}
              label={Locale.translate(Translation.TC_ASSET_TYPE)}
              value={productSectorMapping.assetType}
              onUpdate={updateHandler('assetType')}
              validationIssues={validationIssues}
              canClear={true}
              fieldName={'assetType'}
              getOptionText={AssetTypeHelper.getName}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <RefinitivAssetTypeSelectboxField
              dataTestId={'SectorMappingDialog-RefinitivAssetTypeSelectboxField-AssetType'}
              label={Locale.translate(Translation.TC_REFINITIV_ASSET_TYPE)}
              value={productSectorMapping.refinitivAssetType}
              onUpdate={updateHandler('refinitivAssetType')}
              validationIssues={validationIssues}
              fieldName={'refinitivAssetType'}
              canClear={true}
            />
          </DialogField>

          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-AssetSubType'}
              label={Locale.translate(Translation.TC_ASSET_SUB_TYPE)}
              value={productSectorMapping.assetSubType}
              onUpdate={updateHandler('assetSubType')}
              validationIssues={validationIssues}
              fieldName="assetSubType"
              maxLength={25}
              autoCapitalise={true}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <CountrySelectboxField
              dataTestId={'SectorMappingDialog-CountrySelectboxField-CountryOfIncorporation'}
              label={Locale.translate(Translation.TC_COUNTRY_OF_INCORPORATION)}
              value={productSectorMapping.countryOfIncorporation}
              onUpdate={updateHandler('countryOfIncorporation')}
              validationIssues={validationIssues}
              fieldName={'countryOfIncorporation'}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <CountrySelectboxField
              dataTestId={'SectorMappingDialog-CountrySelectboxField-NotCountryOfIncorporation'}
              label={Locale.translate(Translation.TC_NOT_COUNTRY_OF_INCORPORATION)}
              value={productSectorMapping.notCountryOfIncorporation}
              onUpdate={updateHandler('notCountryOfIncorporation')}
              validationIssues={validationIssues}
              fieldName={'notCountryOfIncorporation'}
              canClear={true}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <CountrySelectboxField
              dataTestId={'SectorMappingDialog-CountrySelectboxField-CountryOfIssuance'}
              label={Locale.translate(Translation.TC_COUNTRY_OF_ISSUANCE)}
              value={productSectorMapping.countryOfIssuance}
              onUpdate={updateHandler('countryOfIssuance')}
              validationIssues={validationIssues}
              fieldName={'countryOfIssuance'}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <CountrySelectboxField
              dataTestId={'SectorMappingDialog-CountrySelectboxField-NotCountryOfIssuance'}
              label={Locale.translate(Translation.TC_NOT_COUNTRY_OF_ISSUANCE)}
              value={productSectorMapping.notCountryOfIssuance}
              onUpdate={updateHandler('notCountryOfIssuance')}
              validationIssues={validationIssues}
              fieldName={'notCountryOfIssuance'}
              canClear={true}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <CurrencySelectboxField
              dataTestId={'SectorMappingDialog-TextInputField-Currency'}
              label={Locale.translate(Translation.TC_CURRENCY)}
              value={productSectorMapping.currency}
              onUpdate={updateHandler('currency')}
              validationIssues={validationIssues}
              fieldName={'currency'}
              canClear={true}
            />
          </DialogField>

          <DialogField>
            <CurrencySelectboxField
              dataTestId={'SectorMappingDialog-TextInputField-NotCurrency'}
              label={Locale.translate(Translation.TC_NOT_CURRENCY)}
              value={productSectorMapping.notCurrency}
              onUpdate={updateHandler('notCurrency')}
              validationIssues={validationIssues}
              fieldName={'notCurrency'}
              canClear={true}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-GovernmentBondType'}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_GOVERNMENT_BOND_TYPE)}
              value={productSectorMapping.governmentBondType}
              onUpdate={updateHandler('governmentBondType')}
              validationIssues={validationIssues}
              fieldName="governmentBondType"
              maxLength={25}
              autoCapitalise={true}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-NotGovernmentBondType'}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_NOT_GOVERNMENT_BOND_TYPE)}
              value={productSectorMapping.notGovernmentBondType}
              onUpdate={updateHandler('notGovernmentBondType')}
              validationIssues={validationIssues}
              fieldName="notGovernmentBondType"
              maxLength={25}
              autoCapitalise={true}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-IssuerLei'}
              label={Locale.translate(Translation.TC_ISSUER_LEI)}
              value={productSectorMapping.issuerLei}
              onUpdate={updateHandler('issuerLei')}
              validationIssues={validationIssues}
              fieldName="issuerLei"
              maxLength={20}
              autoCapitalise={true}
            />
          </DialogField>

          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-NotIssuerLei'}
              label={Locale.translate(Translation.TC_NOT_ISSUER_LEI)}
              value={productSectorMapping.notIssuerLei}
              onUpdate={updateHandler('notIssuerLei')}
              validationIssues={validationIssues}
              fieldName="notIssuerLei"
              maxLength={20}
              autoCapitalise={true}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <CountrySelectboxField
              dataTestId={'SectorMappingDialog-CountrySelectboxField-Domicile'}
              label={Locale.translate(Translation.TC_DOMICILE)}
              value={productSectorMapping.domicile}
              onUpdate={updateHandler('domicile')}
              validationIssues={validationIssues}
              fieldName={'domicile'}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              dataTestId={'SectorMappingDialog-TextInputField-DebtType'}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_DEBT_TYPE)}
              value={productSectorMapping.debtType}
              onUpdate={updateHandler('debtType')}
              validationIssues={validationIssues}
              fieldName="debtType"
              maxLength={25}
              autoCapitalise={true}
            />
          </DialogField>
          <DialogField>
            <BooleanSelectboxField
              dataTestId={'SectorMappingDialog-SelectboxField-BaseIndexNull'}
              value={productSectorMapping.baseIndexNull}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_BASE_INDEX)}
              onUpdate={updateHandler('baseIndexNull')}
              validationIssues={validationIssues}
              fieldName={'baseIndexNull'}
              canClear={true}
            />
          </DialogField>

          <DialogField>
            <BooleanSelectboxField
              dataTestId={'SectorMappingDialog-SelectboxField-HasCUSIP'}
              value={productSectorMapping.hasCUSIP}
              label={Locale.translate(Translation.TC_SECTOR_MAPPING_HAS_CUSIP)}
              onUpdate={updateHandler('hasCUSIP')}
              validationIssues={validationIssues}
              fieldName={'hasCUSIP'}
              canClear={true}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'SectorDialog-TextButton-CancelButton'} type={TextButtonType.Tertiary} onClick={onClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'SectorDialog-TextButton-SaveButton'}
              type={TextButtonType.Primary}
              disabled={validationIssues.length > 0}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
